import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_PUSH_API_ORIGIN } from "./types.js";
import { useAdminPush } from "./push.js";
import { resetPwaApp } from "./register.js";
/**
 * In-app notification inbox backed by g1-push `/app/notifications`. Polls the
 * unread count on an interval (paused while the tab is hidden, refetched on
 * focus — the house pattern; live delivery is the push path). The full list is
 * fetched on demand via `refresh()`.
 */
export function useNotifications({ pushApiOrigin = DEFAULT_PUSH_API_ORIGIN, pollMs = 60000, } = {}) {
    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const timer = useRef(null);
    const fetchUnread = useCallback(async () => {
        try {
            const res = await fetch(`${pushApiOrigin}/app/notifications/unread-count`, {
                credentials: "include",
            });
            if (!res.ok)
                return;
            const body = (await res.json());
            setUnreadCount(body.unreadCount ?? 0);
        }
        catch {
            // transient — keep last known count
        }
    }, [pushApiOrigin]);
    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${pushApiOrigin}/app/notifications?limit=30`, {
                credentials: "include",
            });
            if (!res.ok)
                throw new Error(`notifications ${res.status}`);
            const body = (await res.json());
            setItems(body.data ?? []);
            setUnreadCount(body.unreadCount ?? 0);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setLoading(false);
        }
    }, [pushApiOrigin]);
    const markRead = useCallback(async (id) => {
        setItems((prev) => prev.map((n) => (n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n)));
        setUnreadCount((c) => Math.max(0, c - 1));
        try {
            await fetch(`${pushApiOrigin}/app/notifications/${encodeURIComponent(id)}/read`, {
                method: "POST",
                credentials: "include",
            });
        }
        catch {
            // optimistic — re-sync on next refresh
        }
    }, [pushApiOrigin]);
    const markAllRead = useCallback(async () => {
        setItems((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })));
        setUnreadCount(0);
        try {
            await fetch(`${pushApiOrigin}/app/notifications/read-all`, {
                method: "POST",
                credentials: "include",
            });
        }
        catch {
            // optimistic
        }
    }, [pushApiOrigin]);
    // Poll unread-count; pause when hidden, refetch on focus.
    useEffect(() => {
        fetchUnread();
        function start() {
            if (timer.current)
                return;
            timer.current = setInterval(fetchUnread, pollMs);
        }
        function stop() {
            if (timer.current) {
                clearInterval(timer.current);
                timer.current = null;
            }
        }
        function onVisibility() {
            if (document.hidden) {
                stop();
            }
            else {
                fetchUnread();
                start();
            }
        }
        if (!document.hidden)
            start();
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("focus", fetchUnread);
        return () => {
            stop();
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("focus", fetchUnread);
        };
    }, [fetchUnread, pollMs]);
    return { items, unreadCount, loading, error, refresh, markRead, markAllRead };
}
function BellIcon({ size = 18 }) {
    return (_jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("path", { d: "M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" }), _jsx("path", { d: "M10.3 21a1.94 1.94 0 0 0 3.4 0" })] }));
}
/**
 * Topbar notifications bell + unread badge for the app-shell `notifications`
 * slot. Click toggles the inbox panel (mark-read, deep-link, enable-push toggle).
 */
export function NotificationBell({ appId, pushApiOrigin, onNavigate }) {
    const [open, setOpen] = useState(false);
    const notif = useNotifications({ pushApiOrigin });
    const push = useAdminPush({ appId, pushApiOrigin });
    useEffect(() => {
        if (open)
            notif.refresh();
    }, [open]);
    // Self-heal: if permission is already granted but there's no active
    // subscription (a prior subscribe failed, or a new device), re-subscribe once
    // on mount. Permission is already granted, so this prompts nothing.
    const healed = useRef(false);
    useEffect(() => {
        if (healed.current)
            return;
        if (push.supported && push.permission === "granted" && !push.subscribed && !push.busy) {
            healed.current = true;
            push.subscribe();
        }
    }, [push.supported, push.permission, push.subscribed, push.busy, push.subscribe]);
    function go(url) {
        if (!url)
            return;
        const absolute = /^https?:\/\//.test(url);
        if (!absolute && onNavigate)
            onNavigate(url);
        else
            window.location.assign(url);
    }
    return (_jsxs("div", { style: { position: "relative" }, children: [_jsxs("button", { type: "button", onClick: () => setOpen((v) => !v), className: "relative rounded p-1.5 text-muted hover:bg-surface-2 hover:text-content", "aria-label": "Notifications", title: "Notifications", children: [_jsx(BellIcon, {}), notif.unreadCount > 0 && (_jsx("span", { style: {
                            position: "absolute",
                            top: 0,
                            right: 0,
                            minWidth: 16,
                            height: 16,
                            padding: "0 4px",
                            borderRadius: 8,
                            background: "#ef4444",
                            color: "white",
                            fontSize: 10,
                            fontWeight: 700,
                            lineHeight: "16px",
                            textAlign: "center",
                        }, children: notif.unreadCount > 99 ? "99+" : notif.unreadCount }))] }), open && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", "aria-label": "Close notifications", onClick: () => setOpen(false), style: { position: "fixed", inset: 0, zIndex: 40, background: "transparent", border: 0 } }), _jsx("div", { style: {
                            position: "absolute",
                            right: 0,
                            top: "calc(100% + 8px)",
                            zIndex: 50,
                            width: 360,
                            maxWidth: "90vw",
                            maxHeight: "70vh",
                            overflow: "auto",
                            borderRadius: 10,
                            boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
                        }, className: "border border-line bg-surface", children: _jsx(NotificationPanel, { notif: notif, push: push, onItemClick: go }) })] }))] }));
}
function NotificationPanel({ notif, push, onItemClick }) {
    return (_jsxs("div", { className: "text-content", children: [_jsxs("div", { className: "flex items-center justify-between border-b border-line px-4 py-2.5", children: [_jsx("span", { className: "text-sm font-semibold", children: "Notifications" }), notif.unreadCount > 0 && (_jsx("button", { type: "button", onClick: () => notif.markAllRead(), className: "text-xs text-muted hover:text-content", children: "Mark all read" }))] }), push.supported && !push.subscribed && push.permission !== "denied" && (_jsxs("button", { type: "button", disabled: push.busy, onClick: () => push.subscribe(), className: "flex w-full items-center gap-2 border-b border-line px-4 py-2.5 text-left text-xs text-primary hover:bg-surface-2", children: [_jsx(BellIcon, { size: 14 }), push.busy ? "Enabling…" : "Enable push notifications"] })), push.supported && push.permission === "denied" && !push.subscribed && (_jsx("div", { className: "border-b border-line px-4 py-2.5 text-xs text-muted", children: "Notifications blocked in browser settings." })), notif.loading && _jsx("div", { className: "px-4 py-6 text-center text-xs text-muted", children: "Loading\u2026" }), !notif.loading && notif.items.length === 0 && (_jsx("div", { className: "px-4 py-8 text-center text-xs text-muted", children: "No notifications" })), _jsx("ul", { style: { listStyle: "none", margin: 0, padding: 0 }, children: notif.items.map((n) => (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => {
                            if (!n.readAt)
                                notif.markRead(n.id);
                            onItemClick(n.url);
                        }, className: "flex w-full flex-col gap-0.5 border-b border-line px-4 py-2.5 text-left hover:bg-surface-2", style: { background: n.readAt ? undefined : "var(--g1-color-primary-soft, rgba(59,130,246,0.06))" }, children: [_jsx("span", { className: "text-xs font-medium text-content", children: n.title }), n.body && _jsx("span", { className: "text-[11px] text-muted", children: n.body }), _jsx("span", { className: "text-[10px] text-muted", children: new Date(n.createdAt).toLocaleString() })] }) }, n.id))) }), _jsx("button", { type: "button", onClick: () => resetPwaApp(), className: "flex w-full items-center justify-center gap-1.5 border-t border-line px-4 py-2 text-[11px] text-muted hover:bg-surface-2 hover:text-content", title: "Clear caches + service worker and reload the latest version", children: "\u21BB Update to latest version" })] }));
}
//# sourceMappingURL=notifications.js.map